

const styles = css`
  :host {
    display: inline-block;
    max-width: 960px;
    padding: 8px;
    width: 100%;
  }

  :host([hidden]) {
    display: none;
  }

  .bar {
    background: var(--prompt-bg, #212121);
    border: 1px solid var(--prompt-border, #333333);
    border-radius: 18px;
    box-sizing: border-box;
    color: var(--prompt-fg, #d4d4d4);
    display: grid;
    gap: 14px;
    min-height: 86px;
    padding: 16px 18px;
    width: 100%;
  }

  .question {
    font-size: 0.98rem;
    font-weight: 600;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  .footer {
    align-items: end;
    display: flex;
    gap: 16px;
    justify-content: space-between;
  }

  .answers {
    display: grid;
    gap: 10px;
    min-width: 0;
    width: 100%;
  }

  .options {
    min-width: 0;
  }

  .options label {
    align-items: center;
    color: var(--prompt-control-fg, #bdbdbd);
    cursor: pointer;
    display: inline-flex;
    font-size: 0.9rem;
    gap: 8px;
    margin: 0 14px 8px 0;
  }

  input[type="text"] {
    background: transparent;
    border: 1px solid var(--prompt-control-border, #343434);
    border-radius: 12px;
    box-sizing: border-box;
    color: var(--prompt-fg, #d4d4d4);
    font: inherit;
    font-size: 0.9rem;
    height: 38px;
    outline: none;
    padding: 0 12px;
    width: 100%;
  }

  input[type="text"]::placeholder {
    color: var(--prompt-muted, #747474);
  }

  input[type="text"]:focus {
    border-color: var(--prompt-focus-border, #3c3c3c);
    box-shadow: 0 0 0 3px var(--prompt-ring, rgb(255 255 255 / 7%));
  }

  button {
    background: var(--prompt-send-ready-bg, #f4f4f5);
    border: 1px solid var(--prompt-send-ready-bg, #f4f4f5);
    border-radius: 999px;
    color: var(--prompt-send-ready-fg, #18181b);
    cursor: pointer;
    flex: 0 0 auto;
    font: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    height: 36px;
    min-width: 88px;
    padding: 0 16px;
    transition:
      box-shadow 140ms ease,
      opacity 140ms ease;
  }

  button:focus-visible {
    box-shadow: 0 0 0 3px var(--prompt-ring, rgb(255 255 255 / 7%));
    outline: none;
  }

  button:disabled {
    cursor: default;
    opacity: 0.6;
  }

  @media (max-width: 640px) {
    .footer {
      align-items: stretch;
      flex-direction: column;
    }

    button {
      align-self: flex-end;
    }
  }
`;

function submitAnswer(host: HTMLElement, root: ShadowRoot): void {
  const custom = root.querySelector<HTMLInputElement>('input[type="text"]')?.value.trim() ?? "";
  const picked = root.querySelector<HTMLInputElement>('input[type="radio"]:checked');
  const answer = custom.length > 0 ? custom : (picked?.value ?? "");
  if (!answer) return;

  const customInput = root.querySelector<HTMLInputElement>('input[type="text"]');
  if (customInput) customInput.value = "";
  if (picked) picked.checked = false;

  host.dispatchEvent(
    new CustomEvent("quiz-response", { bubbles: true, composed: true, detail: { answer } }),
  );
}

export class AppQuizBar extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppQuizBar)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["question","data-question","options","data-options","disabled","data-disabled"];

  #question = "";
  #options = [];
  #disabled = false;
  #questionSet = false;
  #optionsSet = false;
  #disabledSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #lists = {};
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get question() {
    return this.#question;
  }

  set question(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#question, next)) return;
    this.#question = next;
    this.#questionSet = true;
    this.#updateBindings('question');
  }

  get options() {
    return this.#options;
  }

  set options(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#options, next)) return;
    this.#options = next;
    this.#optionsSet = true;
    this.#updateBindings('options');
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-question')) this.question = this.dataset.question;
    if (this.hasAttribute('data-options')) this.options = this.dataset.options;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.question = props.question;
      this.options = props.options;
      this.disabled = props.disabled;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#questionSet) this.question = this.dataset.question;
    if (!this.#optionsSet) this.options = this.dataset.options;
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'question':
      case 'data-question':
        this.question = newValue;
        break;
      case 'options':
      case 'data-options':
        this.options = newValue;
        break;
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="bar" role="group" aria-label="Question"><div class="question"><!--argon-text:0-->${this.#escText(this.#question)}<!--/argon-text:0--></div><div class="footer"><div class="answers"><div class="options"><!--argon-text:1-->${this.#options.map((option) => `<label><input type="radio" name="quiz-option" value="${this.#escAttr(option)}"${this.#disabled ? ' disabled' : ''}><span>${this.#escText(option)}</span></label>`).join("")}<!--/argon-text:1--></div><input type="text" placeholder="Custom answer"${this.#disabled ? ' disabled' : ''} data-argon-bind="0"></div><button type="button"${this.#disabled ? ' disabled' : ''} data-argon="3891" data-argon-bind="1">Continue</button></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #adoptList(start, end, htmls) {
    const specs = [];
    let node = start.nextSibling;
    for (const html of htmls) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const nodes = [];
      const anchor = node;
      for (const fresh of template.content.childNodes) {
        if (!node || node === end || node.nodeType !== fresh.nodeType || (node.nodeType === 1 && node.localName !== fresh.localName)) return;
        nodes.push(node);
        node = node.nextSibling;
      }
      specs.push({ anchor, nodes });
    }
    if (node !== end) return;
    return specs.map(({ anchor, nodes }) => {
      const mark = document.createComment('argon-item');
      (anchor ?? end).before(mark);
      return [mark, ...nodes];
    });
  }

  #reconcileList(id, start, end, items) {
    if (!start || !end) return;
    let list = this.#lists[id];
    if (!list) {
      const htmls = items.map(String);
      const nodes = this.#hydrating ? this.#adoptList(start, end, htmls) : undefined;
      if (nodes) list = this.#lists[id] = { htmls, marks: nodes.map(nodes => nodes[0]) };
      else {
        let node = start.nextSibling;
        while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
        list = this.#lists[id] = { htmls: [], marks: [] };
      }
    }
    const { htmls, marks } = list;
    if (items.length < htmls.length) {
      let node = marks[items.length];
      while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
      htmls.length = items.length;
      marks.length = items.length;
    }
    for (let i = 0; i < items.length; i++) {
      const html = String(items[i]);
      if (i < htmls.length) {
        const stop = i + 1 < marks.length ? marks[i + 1] : end;
        if (htmls[i] === html) {
          continue;
        }
        let node = marks[i].nextSibling;
        while (node && node !== stop) { const next = node.nextSibling; node.remove(); node = next; }
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        stop.before(template.content);
        htmls[i] = html;
      } else {
        const mark = document.createComment('argon-item');
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        end.before(mark, template.content);
        marks.push(mark);
        htmls.push(html);
      }
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'question') {
      const __argonNext = `${this.#escText(this.#question)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'options' || prop === 'disabled') {
      this.#reconcileList(1, this.#textStart1, this.#textEnd1, this.#options.map((option) => `<label><input type="radio" name="quiz-option" value="${this.#escAttr(option)}"${this.#disabled ? ' disabled' : ''}><span>${this.#escText(option)}</span></label>`));
    }
    if ((prop === undefined || prop === 'disabled') && this.#attr0) {
      this.#attr0.toggleAttribute('disabled', !!(this.#disabled));
    }
    if ((prop === undefined || prop === 'disabled') && this.#attr1) {
      this.#attr1.toggleAttribute('disabled', !!(this.#disabled));
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3891').forEach(el => this.#listen(el, '3891', 'click', () => submitAnswer(this, root)));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
